import React, { useEffect, useState } from "react";
import { Paper, Typography, TextField, Grid, Button, Select, MenuItem } from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { getTenantSettings, updateTenantSettings, TenantSettings, uploadLogo } from "../../../services/settings";

export default function BrandingTemplatesPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const canEdit = role === "ADMIN" || role === "MANAGER";

  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        setError(null);
        const s = await getTenantSettings(token);
        // Fill defaults for older tenants
        const merged: TenantSettings = {
          locale: s.locale || "en-US",
          timezone: s.timezone || "UTC",
          currency: {
            code: s.currency?.code || "USD",
            symbol: s.currency?.symbol || "$",
            decimalSeparator: s.currency?.decimalSeparator || ".",
            thousandSeparator: s.currency?.thousandSeparator || ",",
          },
          numbering: {
            ticket: {
              prefix: s.numbering?.ticket?.prefix || "T-",
              padding: s.numbering?.ticket?.padding ?? 5,
              next: s.numbering?.ticket?.next ?? 1,
            },
            invoice: {
              prefix: s.numbering?.invoice?.prefix || "INV-",
              padding: s.numbering?.invoice?.padding ?? 5,
              next: s.numbering?.invoice?.next ?? 1,
            },
            purchase: {
              prefix: s.numbering?.purchase?.prefix || "PO-",
              padding: s.numbering?.purchase?.padding ?? 5,
              next: s.numbering?.purchase?.next ?? 1,
            },
          },
          branding: {
            logoLightUrl: s.branding?.logoLightUrl || "",
            logoDarkUrl: s.branding?.logoDarkUrl || "",
            primaryColor: s.branding?.primaryColor || "#1e3a8a",
            secondaryColor: s.branding?.secondaryColor || "#22d3ee",
          },
          templates: {
            invoiceTemplate: s.templates?.invoiceTemplate || "classic",
            ticketTemplate: s.templates?.ticketTemplate || "classic",
          },
        } as TenantSettings;
        setSettings(merged);
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load settings");
      }
    })();
  }, [token]);

  const update = (path: string, value: any) => {
    if (!settings) return;
    const next = { ...settings } as any;
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]] = { ...cur[keys[i]] };
    cur[keys[keys.length - 1]] = value;
    setSettings(next);
  };

  const save = async () => {
    if (!token || !settings) return;
    try {
      setSaving(true);
      setOk(null); setError(null);
      const updated = await updateTenantSettings(token, settings);
      setSettings(updated);
      setOk("Saved");
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <Typography variant="h5" fontWeight={900} gutterBottom>Branding & Templates</Typography>

      {error && (
        <Paper elevation={0} className="p-3 mb-4 rounded-xl" sx={{ border: '1px solid', borderColor: 'error.main', bgcolor: 'rgba(239,68,68,0.08)' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      {ok && (
        <Paper elevation={0} className="p-3 mb-4 rounded-xl" sx={{ border: '1px solid', borderColor: 'success.main', bgcolor: 'rgba(16,185,129,0.08)' }}>
          <Typography color="success.main">{ok}</Typography>
        </Paper>
      )}

      {!settings ? (
        <Paper elevation={0} className="p-4 rounded-2xl" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
          Loading…
        </Paper>
      ) : (
        <>
          <Paper elevation={0} className="p-4 rounded-2xl mb-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>Branding</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField size="small" fullWidth label="Logo (light) URL" value={settings.branding?.logoLightUrl || ''} onChange={(e)=>update('branding.logoLightUrl', e.target.value)} disabled={!canEdit} />
                {canEdit && (
                  <div className="mt-2">
                    <input id="logo-light" type="file" accept="image/*" hidden onChange={async (e)=>{
                      const f = e.target.files?.[0]; if (!f || !token) return; 
                      try { const { url } = await uploadLogo(token, f, 'light'); update('branding.logoLightUrl', url); setOk('Uploaded light logo'); } catch { setError('Upload failed'); }
                    }} />
                    <Button variant="outlined" size="small" onClick={()=>document.getElementById('logo-light')?.click()}>Upload Light Logo</Button>
                  </div>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField size="small" fullWidth label="Logo (dark) URL" value={settings.branding?.logoDarkUrl || ''} onChange={(e)=>update('branding.logoDarkUrl', e.target.value)} disabled={!canEdit} />
                {canEdit && (
                  <div className="mt-2">
                    <input id="logo-dark" type="file" accept="image/*" hidden onChange={async (e)=>{
                      const f = e.target.files?.[0]; if (!f || !token) return; 
                      try { const { url } = await uploadLogo(token, f, 'dark'); update('branding.logoDarkUrl', url); setOk('Uploaded dark logo'); } catch { setError('Upload failed'); }
                    }} />
                    <Button variant="outlined" size="small" onClick={()=>document.getElementById('logo-dark')?.click()}>Upload Dark Logo</Button>
                  </div>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField size="small" fullWidth label="Primary Color" placeholder="#1e3a8a" value={settings.branding?.primaryColor || ''} onChange={(e)=>update('branding.primaryColor', e.target.value)} disabled={!canEdit} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField size="small" fullWidth label="Secondary Color" placeholder="#22d3ee" value={settings.branding?.secondaryColor || ''} onChange={(e)=>update('branding.secondaryColor', e.target.value)} disabled={!canEdit} />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} className="p-4 rounded-2xl mb-6" sx={{ backgroundColor: (t)=>t.palette.background.paper, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={800} gutterBottom>Templates</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Select fullWidth size="small" value={settings.templates?.invoiceTemplate || 'classic'} onChange={(e)=>update('templates.invoiceTemplate', e.target.value)} disabled={!canEdit}>
                  <MenuItem value="classic">Invoice — Classic</MenuItem>
                  <MenuItem value="compact">Invoice — Compact</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} md={6}>
                <Select fullWidth size="small" value={settings.templates?.ticketTemplate || 'classic'} onChange={(e)=>update('templates.ticketTemplate', e.target.value)} disabled={!canEdit}>
                  <MenuItem value="classic">Ticket — Classic</MenuItem>
                  <MenuItem value="compact">Ticket — Compact</MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Paper>

          <div className="flex justify-end">
            <Button onClick={save} disabled={!canEdit || saving} variant="contained">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      )}
    </div>
  );
}
